import { useCallback, useEffect, useState } from 'react';
import { getOfferLabels, getPortalOffer, PortalOffer } from '../api/portalService';

export type UsePortalOfferResult = {
  data: PortalOffer | null;
  labels: Record<string, string>;
  loading: boolean;
  error: Error | null;
  /**
   * El pedido ya terminó (con o sin oferta, con o sin error). Sirve para
   * distinguir «todavía no arrancó» de «terminó y no trajo nada»: con `loading`
   * solo, el primer render después de que el estado resuelve a `offer_issued`
   * tiene `loading === false` porque el efecto aún no corrió, y el panel
   * mostraba el error antes de haber pedido nada.
   */
  settled: boolean;
  refetch: () => void;
};

/**
 * Loads the open offer + exclusion labels when the application is `offer_issued`.
 * Skips the network when `enabled` is false.
 */
export function usePortalOffer(
  quoteId: string,
  options?: { enabled?: boolean },
): UsePortalOfferResult {
  const enabled = (options?.enabled ?? true) && Boolean(quoteId.trim());
  const [data, setData] = useState<PortalOffer | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [settled, setSettled] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setSettled(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSettled(false);
    setError(null);

    Promise.all([getPortalOffer(quoteId), getOfferLabels()])
      .then(([offer, catalog]) => {
        if (cancelled) return;
        setData(offer);
        setLabels(catalog);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(err instanceof Error ? err : new Error('Failed to load portal offer'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setSettled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [quoteId, enabled, nonce]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, labels, loading, error, settled, refetch };
}
